import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useEnhancedChatStore, EnhancedChatMessage } from './enhanced-store';
import { usePitchChatStore } from './pitch-chat-store';
import { Journey } from 'src/common/interfaces/journey.interface';
import { LayoutMode } from '../types';
import { ThumbUpIcon, ThumbDownIcon, RefreshIcon, ArrowRightIcon as SendIcon } from '@heroicons/react/outline';

type Props = {
  opened: boolean;
  journeyUUID: string;
  assistantName?: string;
  personality?: string;
  onSourceClick?: (message: EnhancedChatMessage) => void;
  onDialogClose?: () => void;
  findBlockByContentUuid?: (uuid: string) => any;
  findSectionById?: (id: string) => any;
  layoutMode?: LayoutMode;
  isReadOnly?: boolean;
  visitorEmail?: string;
  userContext?: Record<string, any>;
  // Pitch mode specific props
  isPitchMode?: boolean;
  onNavigateToSection?: (sectionId: string) => void;
  onNavigateToStep?: (stepId: string) => void;
  journey?: Journey;
};

export const PitchChatMessages = ({
  opened,
  journeyUUID,
  assistantName = 'Journey Assistant',
  personality = '',
  onSourceClick,
  onDialogClose,
  findBlockByContentUuid,
  findSectionById,
  layoutMode = 'web',
  isReadOnly = false,
  visitorEmail = '',
  userContext = {},
  isPitchMode = true,
  onNavigateToSection,
  onNavigateToStep,
  journey,
}: Props) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  const {
    conversationId,
    messages,
    isLoading,
    isThinking,
    error,
    inputValue,
    potentialQuestions,
    conversationMetrics,
    isInitialized,
    isInitializing,
    
    startConversation,
    sendMessage,
    setInputValue,
    clearError,
  } = useEnhancedChatStore();

  const {
    startPitchConversation,
    sendPitchMessage,
    navigateToPitchContent,
    setNavigationHandlers,
    initializeFromJourney,
    disabled,
  } = usePitchChatStore();

  // Initialize conversation when component opens (only if not disabled)
  useEffect(() => {
    if (opened && journeyUUID && !disabled) {
      if (isPitchMode) {
        startPitchConversation(journeyUUID, visitorEmail, userContext);
      } else {
        startConversation(journeyUUID, visitorEmail, userContext);
      }
    }
  }, [opened, journeyUUID, visitorEmail, userContext, isPitchMode, disabled]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (opened && !isThinking) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened, isThinking]);

  // Set up navigation handlers for pitch mode
  useEffect(() => {
    if (isPitchMode && onNavigateToSection && onNavigateToStep) {
      setNavigationHandlers({
        onNavigateToSection,
        onNavigateToStep,
      });
    }
  }, [isPitchMode, onNavigateToSection, onNavigateToStep, setNavigationHandlers]);

  // Initialize settings from journey data
  useEffect(() => {
    if (journey) {
      initializeFromJourney(journey);
    }
  }, [journey, initializeFromJourney]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    
    const message = inputValue.trim();
    if (message && !isThinking) {
      if (isPitchMode) {
        sendPitchMessage(message);
      } else {
        sendMessage(message);
      }
    }
  };

  const handlePredefinedQuestion = (question: string) => {
    if (disabled || isThinking) return;
    
    if (isPitchMode) {
      sendPitchMessage(question);
    } else {
      sendMessage(question);
    }
  };

  const handleSourceClick = (message: EnhancedChatMessage) => {
    const { source_content_uuid, source_node_uuid } = message.metadata || {};
    
    if (isPitchMode) {
      // Use pitch mode navigation
      navigateToPitchContent(source_content_uuid, source_node_uuid);
    } else {
      // Use enhanced chat navigation
      if (source_content_uuid && findBlockByContentUuid) {
        const block = findBlockByContentUuid(source_content_uuid);
        if (block) {
          onSourceClick?.(message);
        }
      } else if (source_node_uuid && findSectionById) {
        const section = findSectionById(source_node_uuid);
        if (section) {
          if (onNavigateToSection) {
            onNavigateToSection(source_node_uuid);
          } else {
            onSourceClick?.(message);
          }
        }
      }
    }
  };

  const renderMessage = (message: EnhancedChatMessage, index: number) => {
    const isUser = message.role === 'user';
    const hasSource = !!(message.metadata?.source_content_uuid || message.metadata?.source_node_uuid);
    const confidenceScore = message.metadata?.confidence_score || 0;
    
    return (
      <div
        key={message.id}
        className={classNames('mb-4 flex animate-fadeIn', {
          'justify-end': isUser,
          'justify-start': !isUser,
        })}
      >
        {!isUser && (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2 flex-shrink-0">
            <span className="text-blue-600 text-xs font-medium">AI</span>
          </div>
        )}
        <div
          className={classNames('max-w-[80%] rounded-2xl px-4 py-3 shadow-sm', {
            'bg-blue-600 text-white': isUser,
            'bg-white border border-gray-200 text-gray-800': !isUser,
          })}
        >
          <div className="whitespace-pre-wrap break-words leading-relaxed">
            {message.content}
          </div>
          
          {/* Source and confidence information for pitch mode */}
          {!isUser && hasSource && (
            <div className="mt-3 text-xs opacity-75 border-t border-gray-200 pt-2">
              <button
                onClick={() => handleSourceClick(message)}
                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View in Pitch
              </button>
            </div>
          )}
          
          <div className="mt-1 text-xs opacity-50">
            {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
          </div>
        </div>
        {isUser && (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center ml-2 flex-shrink-0">
            <span className="text-white text-xs font-medium">You</span>
          </div>
        )}
      </div>
    );
  };

  if (!opened) {
    return null;
  }

  return (
    <div 
      ref={chatContainerRef}
      className="flex flex-col h-full bg-gray-50 rounded-lg shadow-lg overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b shadow-sm">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <span className="text-blue-600 text-xs font-medium">AI</span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{assistantName}</h3>
            {conversationMetrics.messageCount > 0 && (
              <div className="text-xs text-gray-500">
                {conversationMetrics.messageCount} messages
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onDialogClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
            title="Close chat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 bg-red-50 border-b animate-fadeIn">
          <div className="flex items-center justify-between">
            <div className="text-red-600 text-sm flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-100 transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {disabled ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 flex flex-col items-center">
              <div className="mb-4">
                <svg className="h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                </svg>
              </div>
              <span className="text-lg font-medium">Chatbot is disabled</span>
              <span className="mt-2 text-sm text-gray-400 text-center">The chatbot is currently turned off. Enable it in the settings to start chatting.</span>
            </div>
          </div>
        ) : isInitializing ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 flex flex-col items-center">
              <div className="mb-4">
                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span>Initializing conversation...</span>
              <span className="mt-2 text-sm text-gray-400">Give me a couple seconds. Getting up to date information</span>
            </div>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}
            {isThinking && (
              <div className="mb-4 flex justify-start animate-fadeIn">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2 flex-shrink-0">
                  <span className="text-blue-600 text-xs font-medium">AI</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce delay-100" />
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-200" />
                    </div>
                    <span className="text-sm text-gray-500">{assistantName} is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Potential Questions */}
      {!disabled && potentialQuestions.length > 0 && !isThinking && (
        <div className="p-4 border-t bg-white animate-fadeIn">
          <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Suggested questions:
          </div>
          <div className="flex flex-wrap gap-2">
            {potentialQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => handlePredefinedQuestion(question)}
                className="px-3 text-left py-1.5 text-sm bg-gray-100 border border-gray-200 rounded-full hover:bg-blue-100 hover:border-blue-300 hover:text-blue-700 transition-all duration-200 shadow-sm"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      {!isReadOnly && (
        <div className="p-4 border-t bg-white">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={disabled ? "Chatbot is disabled" : `Ask ${assistantName} about this Pitch...`}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-800 rounded-full shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              disabled={disabled || isThinking || !isInitialized}
            />
            <button
              type="submit"
              disabled={disabled || !inputValue.trim() || isThinking || !isInitialized}
              className="px-4 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors duration-200 shadow-sm disabled:bg-gray-400"
            >
              <SendIcon className="w-4 h-4" />
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
}; 